import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Handshake, Download, DollarSign, Clock, CheckCircle, TrendingUp } from "lucide-react";

interface Referral {
  id: string;
  referral_code: string;
  client_email: string | null;
  sale_amount: number;
  commission_rate: number;
  commission_amount: number;
  status: string;
  created_at: string;
  paid_at: string | null;
}

interface AffiliateCommissionsTabProps {
  referrals: Referral[];
}

export const AffiliateCommissionsTab = ({ referrals }: AffiliateCommissionsTabProps) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [statusFilter, setStatusFilter] = useState("all");

  const totals = useMemo(() => {
    const totalEarned = referrals.reduce((s, r) => s + r.commission_amount, 0);
    const pending = referrals.filter(r => r.status === 'pending' || r.status === 'confirmed').reduce((s, r) => s + r.commission_amount, 0);
    const paid = referrals.filter(r => r.status === 'paid').reduce((s, r) => s + r.commission_amount, 0);
    const thisMonth = referrals
      .filter(r => {
        const d = new Date(r.created_at);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      })
      .reduce((s, r) => s + r.commission_amount, 0);
    return { totalEarned, pending, paid, thisMonth };
  }, [referrals]);

  const filtered = referrals.filter(r => statusFilter === "all" || r.status === statusFilter);

  const exportCSV = () => {
    const headers = ["Date", "Client", "Sale Amount", "Rate", "Commission", "Status"];
    const rows = referrals.map(r => [
      new Date(r.created_at).toLocaleDateString(),
      r.client_email || '-',
      r.sale_amount,
      `${(r.commission_rate * 100).toFixed(0)}%`,
      r.commission_amount,
      r.status,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `commissions_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summaryCards = [
    { label: isRTL ? "إجمالي الأرباح" : "Total Earned", value: totals.totalEarned, icon: TrendingUp, color: "text-primary", bg: "bg-primary/10" },
    { label: isRTL ? "المعلق" : "Pending", value: totals.pending, icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10" },
    { label: isRTL ? "المدفوع" : "Paid Out", value: totals.paid, icon: CheckCircle, color: "text-green-500", bg: "bg-green-500/10" },
    { label: isRTL ? "هذا الشهر" : "This Month", value: totals.thisMonth, icon: DollarSign, color: "text-blue-500", bg: "bg-blue-500/10" },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <Card key={i}>
              <CardContent className="pt-4 pb-3 px-4">
                <div className="flex items-center gap-2.5">
                  <div className={`h-9 w-9 rounded-lg ${card.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`h-4 w-4 ${card.color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{card.label}</p>
                    <p className="text-lg font-bold">{card.value.toLocaleString()} SAR</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Commission History */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle>{isRTL ? "سجل العمولات" : "Commission History"}</CardTitle>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? "الكل" : "All"}</SelectItem>
                  <SelectItem value="pending">{isRTL ? "معلق" : "Pending"}</SelectItem>
                  <SelectItem value="confirmed">{isRTL ? "مؤكد" : "Confirmed"}</SelectItem>
                  <SelectItem value="paid">{isRTL ? "مدفوع" : "Paid"}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={exportCSV}>
                <Download className="h-4 w-4 mr-1" />
                CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              <Handshake className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="font-medium">{isRTL ? "لا توجد عمولات" : "No commissions"}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{isRTL ? "التاريخ" : "Date"}</TableHead>
                  <TableHead>{isRTL ? "العميل" : "Client"}</TableHead>
                  <TableHead>{isRTL ? "مبلغ البيع" : "Sale Amount"}</TableHead>
                  <TableHead>{isRTL ? "النسبة" : "Rate"}</TableHead>
                  <TableHead>{isRTL ? "العمولة" : "Commission"}</TableHead>
                  <TableHead>{isRTL ? "الحالة" : "Status"}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>{r.client_email || '-'}</TableCell>
                    <TableCell>{r.sale_amount.toLocaleString()} SAR</TableCell>
                    <TableCell>{(r.commission_rate * 100).toFixed(0)}%</TableCell>
                    <TableCell className="font-medium">{r.commission_amount.toLocaleString()} SAR</TableCell>
                    <TableCell>
                      <Badge variant={
                        r.status === 'paid' ? 'default' :
                        r.status === 'confirmed' ? 'secondary' : 'outline'
                      }>
                        {r.status === 'paid' ? (isRTL ? "مدفوع" : "Paid") :
                         r.status === 'confirmed' ? (isRTL ? "مؤكد" : "Confirmed") :
                         (isRTL ? "معلق" : "Pending")}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
